import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import type { Package, Preface } from './structuralMetadataDefinition';
import { descriptorsForSourcePackage, getMaterialPackage, getSourcePackage } from './structuralMetadataDefinition';
import { makeStructuralMetadataReserializer } from './structuralMetadataReserializer';
import type { KLV } from './klv';
import type { Timecode } from '../../util/timecode';
import SmpteTimecode from 'smpte-timecode';
import type { Fraction } from '../../util/fractionType';

export function rewriteHeader(
  kagSize: number,
  structuredLogger: StructuredLogger,
  klvs: KLV[],
  removedFrameCount: number,
  origin: number,
  duration: number,
  startTimecode: Timecode | null,
) : KLV[] {
  const parser = makeStructuralMetadataReserializer(structuredLogger);
  for (const klv of klvs) {
    parser.parseKlv(klv);
  }

  const preface = parser.finish();

  const { materialPackage, sourcePackage } = extractMxfTimingInfo(structuredLogger, preface);

  structuredLogger.log({ level: 'info', message: `Current material pack: ${JSON.stringify(materialPackage)}` });
  structuredLogger.log({ level: 'info', message: `Current source pack: ${JSON.stringify(sourcePackage)}` });


  // The material package start time code is the time at the origin point. This
  // means that we need to take into account the change in origin value.
  const originChange = origin - sourcePackage.origin;
  materialPackage.startTimecode += removedFrameCount + originChange;

  materialPackage.origin = 0;
  materialPackage.duration = duration;

  // The source package start time code is the time code for the first frame
  // in file (i.e without taking origin into account). Because of this we
  // only need to shift by the number of physical frames we've removed.
  sourcePackage.startTimecode += removedFrameCount;

  if (startTimecode) {
    overridePackageStartTimecode(materialPackage, startTimecode, sourcePackage.origin);
    overridePackageStartTimecode(sourcePackage, startTimecode, 0);
  }

  sourcePackage.origin = origin;
  sourcePackage.duration = origin + duration;

  structuredLogger.log({ level: 'info', message: `Updated material pack: ${JSON.stringify(materialPackage)}` });
  structuredLogger.log({ level: 'info', message: `Updated source pack: ${JSON.stringify(sourcePackage)}` });

  updateMxfTimingInfo(preface, { materialPackage, sourcePackage });

  return parser.reserialize(kagSize);
}

export function overridePackageStartTimecode(pkg: PackageTimingInfo, startTimecode: Timecode, offset: number) {
  if (pkg.timecodeRate === null) {
    // We can't override the timecode without known the timecode rate
    return;
  }

  const smpteTimecode =
    new SmpteTimecode(
      startTimecode,
      [pkg.timecodeRate.numerator, pkg.timecodeRate.denominator],
      startTimecode.dropFrame
    );

  pkg.startTimecode = smpteTimecode.frameCount + offset;
}

export interface MxfTimingInfo {
  materialPackage: PackageTimingInfo,
  sourcePackage: PackageTimingInfo,
}

export interface PackageTimingInfo {
  origin: number,
  duration: number | undefined,
  startTimecode: number,

  /**
   *  updatePackageTimingInfo will not change this value
   */
  readonly timecodeRate: Fraction | null,
}

export function extractMxfTimingInfo(structuredLogger: StructuredLogger, preface: Preface): MxfTimingInfo {
  const materialPackage = extractPackageTimingInfo(structuredLogger, getMaterialPackage(preface.contentStorageRef));
  const sourcePackage = extractPackageTimingInfo(structuredLogger, getSourcePackage(preface.contentStorageRef));
  return { materialPackage, sourcePackage };
}

function updateMxfTimingInfo(preface: Preface, timingInfo: MxfTimingInfo) {
  updatePackageTimingInfo(getMaterialPackage(preface.contentStorageRef), timingInfo.materialPackage);
  updatePackageTimingInfo(getSourcePackage(preface.contentStorageRef), timingInfo.sourcePackage);
}

// A minor annoyance about MXF in general is that it stores all the information
// about origin/duration/startTimecode multiple times. This allows so for
// inconsistent files to exist. If there are track under one package with
// conflicting values we'll log it as a warning and used the value from the
// track first encountered.
function extractPackageTimingInfo(structuredLogger: StructuredLogger, pkg: Package): PackageTimingInfo {
  let origin : number | undefined;
  let duration : number | undefined;
  let startTimecode : number | undefined;
  let timecodeRate : Fraction | undefined;

  for (const track of pkg.tracks) {
    if (track.type === 'static_track') {
      // Static tracks does not contain any interesting information
      continue;
    }

    const trackOrigin = track.origin ?? 0;
    if ((origin !== undefined) && (origin !== trackOrigin)) {
      structuredLogger.log({ level: 'warn', message: `Inconsistent origin in ${pkg.type} package. Found both ${origin} and ${trackOrigin}` });
    } else {
      origin = trackOrigin;
    }

    const sequence = track.sequenceRef;
    const trackDuration = sequence.duration;
    if ((duration !== undefined) && (duration !== trackDuration)) {
      structuredLogger.log({ level: 'warn', message: `Inconsistent duration in ${pkg.type} package. Found both ${duration} and ${trackDuration}` });
    } else {
      duration = trackDuration;
    }

    const components = sequence.structuralComponents;
    if (components.length != 1) {
      structuredLogger.log({ level: 'warn', message: `Expected a single component inside sequence but found ${components.length}` });
    }
    for (const component of components) {
      if (component.type === 'timecode_component') {
        const componentStartTimecode = component.startTimecode;
        if ((startTimecode !== undefined) && (componentStartTimecode !== startTimecode)) {
          structuredLogger.log({ level: 'warn', message: `Inconsistent startTimecode in ${pkg.type} package. Found both ${startTimecode} and ${componentStartTimecode}` });
        } else {
          startTimecode = componentStartTimecode;
          timecodeRate = track.editRate;
        }
      }
      const componentDuration = component.duration;
      if ((duration !== undefined) && (duration !== componentDuration)) {
        structuredLogger.log({ level: 'warn', message: `Inconsistent duration in ${pkg.type} package. Found both ${duration} and ${componentDuration}` });
      } else {
        duration = componentDuration;
      }
    }
  }

  if (pkg.type === 'source_package') {
    for (const subDescriptor of descriptorsForSourcePackage(pkg)) {
      if (subDescriptor.type === 'unknown_essence_descriptor') {
        continue;
      }
      const containerDuration = subDescriptor.containerDuration;
      if (containerDuration !== undefined) {
        if ((duration !== undefined) && (duration !== containerDuration)) {
          structuredLogger.log({ level: 'warn', message: `Inconsistent duration in ${pkg.type} package. Found both ${duration} and ${containerDuration}` });
        } else {
          duration = containerDuration;
        }
      }
    }
  }

  return {
    duration: duration,
    origin: origin ?? 0,
    startTimecode: startTimecode ?? 0,
    timecodeRate: timecodeRate ?? null,
  };
}

function updatePackageTimingInfo(pkg: Package, timingInfo: PackageTimingInfo) {
  for (const track of pkg.tracks) {
    if (track.type === 'static_track') {
      // Static tracks does not contain any interesting information
      continue;
    }

    track.origin = timingInfo.origin;
    const sequence = track.sequenceRef;
    sequence.duration = timingInfo.duration;

    const components = sequence.structuralComponents;
    for (const component of components) {
      if (component.type === 'timecode_component') {
        component.startTimecode = timingInfo.startTimecode;
      }
      component.duration = timingInfo.duration;
    }
  }

  if (pkg.type === 'source_package') {
    for (const subDescriptor of descriptorsForSourcePackage(pkg)) {
      if (subDescriptor.type === 'unknown_essence_descriptor') {
        continue;
      }
      subDescriptor.containerDuration = timingInfo.duration;
    }
  }
}
